import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { Delete, LocationOn } from '@mui/icons-material';
import { useCreateVenue, useDeleteVenue, useUpdateVenue, useVenue, useVenues } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { CatalogList, ErrorState, LoadingState, useCatalogParams } from '@/components';
import { FormPage, KebabMenu } from '@/components/manage';
import { useNotification } from '@/contexts/NotificationContext';
import type { SportType, Venue } from '@/types';
import { LISTED_SPORT_OPTIONS, getSportOption, sortSportTypes } from '@/utils/sports';
import type { AddressSuggestionOption } from '@/utils/dadata';
import { fetchAddressSuggestions } from '@/utils/dadata';
import { ensureCity, getCityById } from '@/utils/cityRegistry';
import { getApiErrorMessage } from '@/utils/apiError';
import { isUUID } from '@/utils/uuid';

const dadataToken = import.meta.env.VITE_DADATA_TOKEN?.trim() ?? '';

export const VenuesPage: React.FC = () => {
  const { q, page } = useCatalogParams();
  const { adminMode, create } = useAdminMode();
  const { data, isLoading, error, refetch } = useVenues({ page, limit: 20, q });

  return (
    <CatalogList
      title="Площадки"
      icon={<LocationOn color="primary" />}
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      totalPages={data?.totalPages}
      createHref={create.venue ? '/venues/new' : undefined}
      emptyText="Площадок пока нет"
      rows={(data?.data ?? []).map((venue) => ({
        id: venue.id,
        title: venue.name,
        muted: !venue.active,
        href: adminMode ? `/venues/${venue.id}/edit` : undefined,
        subtitle: (
          <>
            {venue.address}
            <Box sx={{ mt: 0.5 }}>
              {venue.cityName && <Chip label={venue.cityName} size="small" sx={{ mr: 0.5 }} />}
              {sortSportTypes(venue.sportTypes || []).map((st) => (
                <Chip key={st} label={getSportOption(st)?.label || st} size="small" sx={{ mr: 0.5 }} />
              ))}
            </Box>
          </>
        ),
      }))}
    />
  );
};

type VenueForm = {
  cityId: string;
  cityLabel: string;
  name: string;
  timezone: string;
  address: string;
  addressMeta: string;
  active: boolean;
  sportTypes: SportType[];
};

const emptyForm: VenueForm = {
  cityId: '',
  cityLabel: '',
  name: '',
  timezone: 'Europe/Moscow',
  address: '',
  addressMeta: '{}',
  active: true,
  sportTypes: [],
};

const fromVenue = (venue: Venue): VenueForm => ({
  cityId: venue.cityId || '',
  cityLabel: venue.cityName || getCityById(venue.cityId || undefined)?.name || '',
  name: venue.name,
  timezone: venue.timezone || 'Europe/Moscow',
  address: venue.address || '',
  addressMeta: venue.addressMeta || '{}',
  active: venue.active,
  sportTypes: venue.sportTypes || [],
});

const VenueFormInner: React.FC<{ venue?: Venue }> = ({ venue }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { can } = useAdminMode();
  const [form, setForm] = useState<VenueForm>(venue ? fromVenue(venue) : emptyForm);
  const [dirty, setDirty] = useState(false);
  const [addressInput, setAddressInput] = useState(venue?.address ?? '');
  const [addressOptions, setAddressOptions] = useState<AddressSuggestionOption[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const createVenue = useCreateVenue();
  const updateVenue = useUpdateVenue();
  const deleteVenue = useDeleteVenue();

  const patch = (next: Partial<VenueForm>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };

  useEffect(() => {
    const query = addressInput.trim();
    if (!dadataToken || query.length < 3) {
      setAddressOptions([]);
      return;
    }
    let active = true;
    const handle = window.setTimeout(async () => {
      setAddressLoading(true);
      setAddressError(false);
      try {
        const options = await fetchAddressSuggestions(query, dadataToken);
        if (active) setAddressOptions(options);
      } catch {
        if (active) setAddressError(true);
      } finally {
        if (active) setAddressLoading(false);
      }
    }, 400);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [addressInput]);

  const submit = async () => {
    const payload = { ...form, cityId: isUUID(form.cityId) ? form.cityId : null };
    try {
      if (venue) await updateVenue.mutateAsync({ id: venue.id, data: payload });
      else await createVenue.mutateAsync(payload);
      setDirty(false);
      showSuccess('Площадка сохранена');
      navigate('/venues', { replace: true });
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось сохранить площадку.'));
    }
  };

  return (
    <FormPage
      title={venue ? 'Площадка' : 'Новая площадка'}
      onSubmit={() => void submit()}
      saving={createVenue.isPending || updateVenue.isPending}
      submitDisabled={!form.name || !form.address || (venue ? !can(venue.permissions?.update) : false)}
      dirty={dirty}
      closeTo="/venues"
      headerActions={
        venue && (
          <KebabMenu
            items={[
              {
                id: 'delete',
                label: 'Удалить',
                icon: <Delete fontSize="small" />,
                danger: true,
                hidden: !can(venue.permissions?.delete),
                confirm: { title: 'Удалить площадку?' },
                onClick: async () => {
                  await deleteVenue.mutateAsync(venue.id);
                  navigate('/venues', { replace: true });
                },
              },
            ]}
          />
        )
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField label="Название" required fullWidth value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        <Autocomplete
          freeSolo
          options={addressOptions.map((o) => o.label)}
          filterOptions={(o) => o}
          value={form.address}
          inputValue={addressInput}
          onInputChange={(_, value) => {
            setAddressInput(value);
            patch({ address: value });
          }}
          onChange={(_, value) => {
            const label = typeof value === 'string' ? value : '';
            setAddressInput(label);
            const selected = addressOptions.find((o) => o.label === label);
            const city = selected?.cityLabel
              ? ensureCity({ label: selected.cityLabel, geoId: selected.geoId, timezone: selected.timezone, meta: selected.meta })
              : undefined;
            patch({
              address: label,
              cityId: city?.id || form.cityId,
              cityLabel: city?.name || form.cityLabel,
              addressMeta: selected?.meta || '{}',
              timezone: city?.timezone || selected?.timezone || form.timezone,
            });
          }}
          noOptionsText={addressError ? 'Ошибка загрузки подсказок' : addressInput.trim().length < 3 ? 'Введите минимум 3 символа' : 'Адреса не найдены'}
          loading={addressLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Адрес"
              required
              helperText={!dadataToken ? 'Подсказки адреса отключены' : undefined}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {addressLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        <TextField label="Город" fullWidth value={form.cityLabel} slotProps={{ input: { readOnly: true } }} helperText="Определяется по адресу" />
        <TextField label="Часовой пояс" fullWidth value={form.timezone} slotProps={{ input: { readOnly: true } }} />
        <FormControl fullWidth>
          <InputLabel>Виды спорта</InputLabel>
          <Select
            multiple
            value={form.sportTypes}
            onChange={(e) => patch({ sportTypes: e.target.value as SportType[] })}
            input={<OutlinedInput label="Виды спорта" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as SportType[]).map((value) => (
                  <Chip key={value} label={getSportOption(value)?.label || value} size="small" />
                ))}
              </Box>
            )}
          >
            {LISTED_SPORT_OPTIONS.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Checkbox checked={form.sportTypes.includes(type.value)} size="small" />
                <ListItemText primary={type.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ minHeight: 48 }}
          control={<Switch checked={form.active} onChange={(e) => patch({ active: e.target.checked })} />}
          label="Активна"
        />
      </Stack>
    </FormPage>
  );
};

export const VenueFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: venue, isLoading, error, refetch } = useVenue(id ?? '', { queryKey: ['venues', id ?? ''], enabled: Boolean(id) });
  if (!id) return <VenueFormInner />;
  if (isLoading) return <LoadingState fullscreen />;
  if (error || !venue) return <ErrorState message="Площадка не найдена" onRetry={refetch} />;
  return <VenueFormInner key={venue.id} venue={venue} />;
};
